/**
 * stockDocumentReturn — devolver ao estoque o que um documento EXCLUÍDO ainda
 * mantinha baixado (gatilho `document_deleted_return`).
 *
 * ─── A ORDEM É O PONTO CRÍTICO: CALCULAR ANTES, APLICAR DEPOIS ─────────────
 *
 * `stock_movements.quote_id` e `.service_order_id` são FK **ON DELETE SET
 * NULL** (verificado no Postgres de produção). Assim que o documento morre, o
 * vínculo se perde e NÃO HÁ MAIS COMO SABER o que devolver — os movimentos
 * viram órfãos indistinguíveis de um ajuste manual. Por isso o CÁLCULO é feito
 * antes do `delete`.
 *
 * A APLICAÇÃO, ao contrário, só pode vir **depois** do delete ter dado certo.
 * Devolver primeiro parecia natural (era o que o app fazia) e produziu o pior
 * defeito possível: quando o delete falhava — e ele falhava de verdade, pelo
 * CHECK da despesa cobrável (ver `src/lib/quoteDeletion.ts`) — a mercadoria já
 * tinha voltado à prateleira de um documento que continuava vivo e cobrando.
 * Estoque inflado, sem erro nenhum além de um toast genérico.
 *
 * Consequência aceita: o movimento de devolução nasce **sem** o vínculo com o
 * documento (a FK não aceitaria um id que não existe mais), então a referência
 * vai no `note`. É o preço de não escrever no estoque por causa de uma exclusão
 * que pode não acontecer.
 *
 * ─── CLASSIFICAÇÃO DOS CAMINHOS QUE APAGAM DOCUMENTO ───────────────────────
 *
 * Varridos um a um; devolver estoque num ROLLBACK seria criar mercadoria que
 * nunca saiu, então a distinção não é cosmética:
 *
 * | Caminho                                        | Classe          | Devolve? |
 * |------------------------------------------------|-----------------|----------|
 * | `NetworkContext` `create_quote` (3 catches)     | rollback        | não      |
 * | `useQuickSale` (venda que falhou)               | rollback        | não      |
 * | `guestQuoteImport` (importação que falhou)      | rollback        | não      |
 * | `NetworkContext` `create_service_order` (2)     | rollback        | não      |
 * | `useServiceOrders` create (2 catches)           | rollback        | não      |
 * | `QuoteDetailPage.handleDelete`                  | EXCLUSÃO REAL   | **sim**  |
 * | `QuotesListPage.handleBulkDelete`               | EXCLUSÃO REAL   | **sim**  |
 * | `NetworkContext` `delete_quote` (fila)          | EXCLUSÃO REAL   | **sim**  |
 * | `ServiceOrderDetailPage` → `useDeleteServiceOrder` | EXCLUSÃO REAL| **sim**  |
 * | `NetworkContext` `delete_service_order` (fila)  | EXCLUSÃO REAL   | **sim**  |
 * | `NetworkContext` `clear_data`                   | apagar a conta  | não      |
 * | `DeleteAccountPage`                             | apagar a conta  | não      |
 *
 * Os rollbacks apagam um documento criado **segundos antes**, que nunca chegou
 * a existir para o usuário e portanto nunca teve baixa (o prompt de baixa exige
 * um id real e uma ação humana). Devolver ali inflaria o estoque.
 *
 * `clear_data`/`DeleteAccountPage` apagam PRODUTOS junto — e
 * `stock_movements.product_id` é CASCADE, então não sobra nem estoque nem
 * movimento para acertar. Devolver antes de apagar tudo seria escrever num
 * saldo que deixa de existir no instante seguinte.
 *
 * ─── IDEMPOTÊNCIA ──────────────────────────────────────────────────────────
 *
 * Não há marcador local: a conta sai do SALDO LÍQUIDO dos movimentos
 * (`netDeductedByProduct`). Rodar duas vezes seguidas devolve zero na segunda,
 * porque o `sale_return` da primeira já zerou o líquido. E depois que o
 * documento é apagado o `SET NULL` faz a consulta não achar mais nada, então
 * uma fila sincronizada duas vezes também não devolve em dobro — o próprio
 * `SET NULL` virou o marcador de "já passou por aqui".
 */
import { supabase } from '@/integrations/supabase/client';
import {
  DOCUMENT_STOCK_REASONS,
  netDeductedByProduct,
  type StockMovementLike,
  type StockReturnLine,
} from '@/lib/stockReturn';

export type DocumentKind = 'quote' | 'service_order';

const COLUMN: Record<DocumentKind, 'quote_id' | 'service_order_id'> = {
  quote: 'quote_id',
  service_order: 'service_order_id',
};

/** Ids reais (um `offline_` nunca teve movimento no servidor). */
function realIds(ids: readonly string[]): string[] {
  return Array.from(
    new Set(
      (ids ?? [])
        .map((id) => String(id ?? ''))
        .filter((id) => id.length > 0 && !id.startsWith('offline_')),
    ),
  );
}

/**
 * Quanto cada documento ainda mantém baixado, por produto.
 *
 * Uma consulta só para a lista inteira (a exclusão em massa pode ter dezenas de
 * orçamentos) e o agrupamento é feito aqui.
 */
export async function fetchDocumentReturnLines(
  companyId: string,
  kind: DocumentKind,
  ids: readonly string[],
): Promise<Map<string, StockReturnLine[]>> {
  const result = new Map<string, StockReturnLine[]>();
  const targets = realIds(ids);
  if (!companyId || targets.length === 0) return result;

  const column = COLUMN[kind];
  const { data, error } = await supabase
    .from('stock_movements')
    .select(`product_id, delta, reason, ${column}`)
    .eq('company_id', companyId)
    .in(column, targets)
    .in('reason', DOCUMENT_STOCK_REASONS as unknown as string[]);
  if (error) throw error;

  const byDocument = new Map<string, StockMovementLike[]>();
  for (const row of (data ?? []) as any[]) {
    const documentId = row?.[column] ? String(row[column]) : '';
    if (!documentId) continue;
    const list = byDocument.get(documentId);
    if (list) list.push(row as StockMovementLike);
    else byDocument.set(documentId, [row as StockMovementLike]);
  }

  for (const [documentId, movements] of byDocument) {
    const lines = netDeductedByProduct(movements);
    if (lines.length > 0) result.set(documentId, lines);
  }

  return result;
}

export interface DocumentReturnResult {
  /** Documentos que tinham algo a devolver. */
  documents: number;
  /** Linhas de produto efetivamente devolvidas. */
  products: number;
}

/** A devolução já CALCULADA, à espera de o documento morrer. */
export interface PreparedDocumentReturn {
  /** Documentos com algo a devolver. */
  documents: number;
  /** Linhas de produto que voltarão ao estoque. */
  products: number;
  /**
   * Aplica a devolução. **Chame só DEPOIS de o delete ter dado certo.**
   *
   * Os movimentos nascem sem o vínculo do documento (ele não existe mais) —
   * a referência fica no `note`, que é o que sobra para auditoria.
   */
  apply: () => Promise<DocumentReturnResult>;
}

/**
 * Lê o que cada documento ainda mantém baixado e devolve o aplicador.
 *
 * **Chame antes do `delete`** (o cálculo depende do vínculo, que o delete
 * destrói) e **aplique depois** (aplicar antes escreve no estoque de um
 * documento que pode não morrer). Ver o cabeçalho deste arquivo.
 */
export async function prepareDocumentStockReturn(
  companyId: string,
  kind: DocumentKind,
  ids: readonly string[],
): Promise<PreparedDocumentReturn> {
  const byDocument = await fetchDocumentReturnLines(companyId, kind, ids);

  let documents = 0;
  let products = 0;
  for (const lines of byDocument.values()) {
    if (lines.length === 0) continue;
    documents += 1;
    products += lines.length;
  }

  const apply = async (): Promise<DocumentReturnResult> => {
    let appliedDocuments = 0;
    let appliedProducts = 0;

    for (const [documentId, lines] of byDocument) {
      let applied = 0;
      for (const line of lines) {
        const { error } = await supabase.rpc('apply_stock_movement', {
          p_product_id: line.product_id,
          // Positivo: a mercadoria volta para a prateleira.
          p_delta: line.quantity,
          p_reason: 'sale_return',
          // Nulos de propósito: a FK recusaria o id de um documento já
          // apagado. A rastreabilidade vai no texto.
          p_quote_id: null,
          p_service_order_id: null,
          p_note: `Devolução por exclusão do documento (${kind === 'quote' ? 'orçamento' : 'OS'} ${documentId})`,
        });
        if (error) throw error;
        applied += 1;
      }
      if (applied > 0) {
        appliedDocuments += 1;
        appliedProducts += applied;
      }
    }

    return { documents: appliedDocuments, products: appliedProducts };
  };

  return { documents, products, apply };
}
